/**
 * One reading of the plan, shared by every screen that shows it.
 *
 * The list, the board, the chart, the sheet and the calendar are five ways of looking
 * at the same rows. They share a query key on purpose: edit a date in the sheet and the
 * chart behind it is already right, because there was only ever one copy. Five screens
 * each fetching their own plan is five screens that disagree for a second after every
 * edit, and that second is where people lose trust in a tool.
 *
 * The mutations live here for the same reason. "What else moved" is the interesting
 * part of every edit, and it has to be reported the same way whether the edit came from
 * dragging a bar or typing in a cell.
 */
import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { useToast } from '../../components/ui'

export type Predecessor = { id: string; key: string; relation: string; lag_days: number }

export type Row = {
  /** Where this row sits in the outline. See `planRows`. */
  order: number
  /** The outline number — 1, 1.1, 1.2 — derived from the tree by the API. */
  wbs: string
  id: string
  key: string
  title: string
  type_key: string
  type_name: string
  depth: number
  parent_id: string | null
  has_children: boolean
  status_name: string
  status_category: string
  /**
   * WHAT THIS READER MAY DO TO THIS ROW, answered by the API per row.
   *
   * These three replace a single `writable` boolean that covered the whole screen and was
   * derived from holding `work.write` in any company at all — which is how the mind map came to
   * offer a customer "Mark done" and "Delete" on work the board beside it showed as locked.
   *
   * `may_move` is the tick box: finishing a task is moving it to Done, so it asks `work.move`,
   * which no client role holds.
   */
  editable: boolean
  may_move: boolean
  may_delete: boolean
  /** The running sprint that fixes this row for this reader. Null for our own people. */
  locked_by: { id: string; name: string } | null
  /** The first of `assignees`, for the grid's one narrow column. */
  assignee_id: string | null
  assignee: string | null
  /** Everybody on it. The task model holds many; the grid can only draw one. */
  assignees: Array<{ id: string; name: string }>
  progress_pct: number | null
  constraint_type: string
  constraint_on: string | null
  constraint_label: string
  deadline_on: string | null
  task_type: string
  /** 'auto' — the plan places it. 'manual' — a person did, and it stays put. */
  scheduling_mode?: string
  effort_driven: boolean
  elapsed: boolean
  /** Stated on the task. `milestone` below is what the chart draws — it also covers a
   *  row that only ever had one date, such as a due date. */
  is_milestone: boolean
  past_deadline_by?: number | null
  priority: string
  estimate_s: number | null
  remaining_s: number | null
  spent_s: number
  planned_cost: string | null
  actual_cost: string | null
  /** What the current baseline agreed, if one has been taken. */
  baseline_start: string | null
  baseline_end: string | null
  start?: string
  end?: string
  milestone?: boolean
  dates_from?: string
  late?: boolean
  days?: number
  working_days?: number
  critical?: boolean
  slack_days?: number | null
  free_slack_days?: number | null
  predecessors?: Predecessor[]
  successors?: Predecessor[]
}

export type Dependency = { from: string; to: string; relation: string; lag_days: number }

export type Plan = {
  project: { id: string; key: string; name: string }
  /**
   * THIS PROJECT'S capabilities, sent the way the board sends its own.
   *
   * The plan had no way to ask: its permission hook answered from every company the person
   * belonged to at once, so being a developer for one customer made another customer's plan
   * editable. Reading them off the payload asks about the project actually on screen.
   */
  capabilities: Record<string, 'full' | 'own' | 'none'>
  /**
   * WHETHER THE READER IS ON THE CUSTOMER'S SIDE — a question no capability may answer, which
   * is why the API sends it rather than the screen inferring one. Their lead holds `work.write`
   * at full, so any capability read here would have said "yes, let them add a task".
   *
   * Adding work straight to the plan is ours; their route in is "Report it", which asks the
   * questions a blank row does not.
   */
  customer: boolean
  today: string
  from: string
  to: string
  rows: Row[]
  unscheduled: Row[]
  releases: { id: string; name: string; on: string; released: boolean; late: boolean }[]
  dependencies: Dependency[]
  truncated: boolean
  /** Weekdays nobody works in the window — public holidays — so the chart can hatch them. */
  holidays?: string[]
  /** What a working day is worth on this project, so a length can be said in hours. */
  day_hours?: number
}

export type Moved = {
  key: string
  title: string
  start: string
  end: string
  was_start: string | null
  was_end: string | null
}

type Change = { key: string; moved: Moved[]; cascaded: boolean; conflicts: string[] }

/**
 * The plan as one list, in outline order.
 *
 * The API keeps scheduled and unscheduled rows apart so a chart never draws a bar
 * nobody put there. A grid has no such excuse: it has to show the plan in the order the
 * plan is in. Concatenating the two lists put every undated task at the bottom, so a
 * task added under a phase turned up below the entire project, detached from the phase
 * it belonged to — which made the phase look as though it had ignored it.
 */
export function planRows(plan: Plan | undefined): Row[] {
  if (!plan) return []
  return [...plan.rows, ...plan.unscheduled].sort((a, b) => a.order - b.order)
}

/**
 * The plan, optionally narrowed to one sprint.
 *
 * The sprint comes from context rather than an argument so the kit's five screens all
 * answer to the board's picker without any of them being told about it. It is part of the
 * cache key — a sprint's plan and the whole plan are two different answers — and
 * `usePlanEdits` invalidates on the `['plan', projectId]` prefix, so an edit made while
 * scoped still refreshes both.
 *
 * Scoping is honest about what it drops: the API builds the outline among the sprint's own
 * work, so a phase whose other children sit outside the sprint is not shown at all rather
 * than shown with a bar covering half of what it holds.
 */
/**
 * WHETHER WORK THAT HAS BEEN FILED AWAY IS DRAWN, remembered per project in this browser.
 *
 * CLOSED, NOT DONE, and the difference matters. A task that is finished but still on the board
 * stays on the plan: it is in this sprint, it is what the work after it was waiting for, and
 * taking it off leaves a gap that reads as missing rather than finished. Only work somebody has
 * deliberately closed — filed away, off the board — is hidden.
 *
 * Hidden by default, which is a change: the plan asked for everything and nothing could say
 * otherwise, so a project that had been running a while drew every task it had ever closed. On
 * the mind map that is the worst of it — a tree where most of the branches are archive is a
 * tree nobody can read.
 *
 * The board already works this way and calls it "Show closed", so the plan matching it is one
 * habit rather than two. Per project because the answer genuinely differs: a plan mid-flight
 * wants the clutter gone, a plan being reviewed at the end wants all of it.
 */
export function useShowClosed(projectId: string): [boolean, (on: boolean) => void] {
  const key = `hub.plan.showClosed.${projectId}`
  const [on, setOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === 'on'
    } catch {
      return false
    }
  })
  const set = useCallback(
    (next: boolean) => {
      setOn(next)
      try {
        localStorage.setItem(key, next ? 'on' : 'off')
      } catch {
        /* a browser that blocks storage still gets the toggle, just not the memory */
      }
    },
    [key],
  )
  return [on, set]
}

export function usePlan(projectId: string, showClosed = false) {
  return useQuery({
    queryKey: ['plan', projectId, showClosed],
    queryFn: () => api.get<Plan>(`/projects/${projectId}/timeline?include_closed=${showClosed}`),
  })
}

/** Every edit a plan screen can make, and one place that says what each one did. */
export function usePlanEdits(projectId: string, onError?: (e: Error) => void) {
  const qc = useQueryClient()
  const toast = useToast()
  const base = `/projects/${projectId}/schedule`

  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['plan', projectId] })
    qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] })
    qc.invalidateQueries({ queryKey: ['roadmap'] })
    qc.invalidateQueries({ queryKey: ['work'] })
  }, [qc, projectId])

  const fail = useCallback(
    (e: Error) => {
      toast(e.message, 'danger')
      onError?.(e)
    },
    [toast, onError],
  )

  /** A drag that pushed four other people's work has to say so. */
  const announce = useCallback(
    (moved: Moved[]) => {
      if (moved.length > 1) {
        toast(
          `${moved[0]!.key} moved, and so did ${moved
            .slice(1)
            .map((m) => m.key)
            .join(', ')}.`,
          'info',
        )
      }
    },
    [toast],
  )

  const done = (result: Change) => {
    announce(result.moved)
    // A constraint that could not be honoured is the one thing a cascade must not do
    // quietly: the task stayed where it was pinned, and somebody has to decide what
    // gives.
    for (const conflict of result.conflicts ?? []) toast(conflict, 'danger')
    refresh()
    return result
  }

  const move = useMutation({
    mutationFn: (input: { key: string; start?: string; end?: string; days?: number; cascade?: boolean }) =>
      api.post<Change>(`${base}/tasks/${input.key}/move`, {
        start_on: input.start ?? null,
        end_on: input.end ?? null,
        duration_days: input.days ?? null,
        cascade: input.cascade ?? true,
      }),
    onSuccess: done,
    onError: fail,
  })

  const amend = useMutation({
    mutationFn: (input: { key: string; body: Record<string, unknown> }) =>
      api.patch<Change>(`${base}/tasks/${input.key}`, input.body),
    onSuccess: done,
    onError: fail,
  })

  const add = useMutation({
    // `parent` is sent only when the caller means to set one. Sending null every time
    // is not the same as leaving it out: null means "put this at the top level", which
    // stopped a new row from taking the level of the row it was added under and left
    // it sitting outside the phase somebody had just selected.
    mutationFn: (input: {
      title: string
      after?: string
      /** Immediately above this row — the only way to reach the top of the plan. */
      before?: string
      parent?: string | null
      atTopLevel?: boolean
      start?: string
      days?: number
    }) =>
      api.post<{ key: string }>(`${base}/tasks`, {
        title: input.title,
        after_key: input.after ?? null,
        before_key: input.before ?? null,
        ...(input.parent !== undefined || input.atTopLevel
          ? { parent_key: input.parent ?? null }
          : {}),
        start_on: input.start ?? null,
        duration_days: input.days ?? null,
      }),
    onSuccess: refresh,
    onError: fail,
  })

  /** Close every gap the links do not call for, and report what that moved. */
  const tighten = useMutation({
    mutationFn: () => api.post<Change>(`${base}/reschedule`, {}),
    onSuccess: (result) => {
      if (!result.moved.length) {
        toast('Nothing to close — every task is already as early as its links allow.', 'info')
      } else {
        toast(
          `${result.moved.length} ${result.moved.length === 1 ? 'task' : 'tasks'} moved to the earliest their links allow.`,
          'success',
        )
      }
      for (const conflict of result.conflicts ?? []) toast(conflict, 'danger')
      refresh()
      return result
    },
    onError: fail,
  })

  const structure = useMutation({
    mutationFn: (input: { key: string; what: 'indent' | 'outdent' }) =>
      api.post<{ promoted_to?: string | null }>(`${base}/tasks/${input.key}/${input.what}`),
    onSuccess: (result) => {
      // Indenting can quietly change what the row above *is*. Saying so beats a type
      // silently changing under somebody who only pressed Tab.
      if (result?.promoted_to) toast(`The row above is now a ${result.promoted_to.toLowerCase()}.`, 'info')
      refresh()
    },
    onError: fail,
  })

  const reorder = useMutation({
    // `reparent` decides whether the indent travels with the drop. Sending parent_key
    // as null means "make this a top-level row", which is a different instruction from
    // not sending it at all, so the caller has to say which it means.
    mutationFn: (input: {
      key: string
      after: string | null
      parent?: string | null
      reparent?: boolean
    }) =>
      api.post(`${base}/tasks/${input.key}/reorder`, {
        after_key: input.after,
        ...(input.reparent ? { parent_key: input.parent ?? null } : {}),
      }),
    onSuccess: refresh,
    onError: fail,
  })

  const remove = useMutation({
    mutationFn: (key: string) => api.del(`${base}/tasks/${key}`),
    onSuccess: refresh,
    onError: fail,
  })

  const tick = useMutation({
    mutationFn: (input: { key: string; done: boolean; resolution?: string }) =>
      api.post<{ status: string; went_through: string[] }>(`${base}/tasks/${input.key}/done`, {
        done: input.done,
        /*
          Finishing a service ticket records what was done, the same as resolving one on the
          board. The caller asks for it when the row says it is owed — see `needs_resolution`.
          The wire name is the port kit's; it carries free text, not a key.
        */
        ...(input.resolution ? { resolution_key: input.resolution } : {}),
      }),
    onSuccess: (result) => {
      // Plenty of workflows have no direct route to done — you have to start a thing
      // before you can finish it — so a tick sometimes takes two steps. It should say
      // so; a status that changed twice without a word is a status nobody trusts.
      if ((result.went_through?.length ?? 0) > 1) {
        toast(`Went through ${result.went_through.join(', then ')}.`, 'info')
      }
      refresh()
    },
    onError: fail,
  })

  const link = useMutation({
    // Also the way an existing link is changed: the endpoint upserts, so setting the
    // relation or the lag on one that already exists is the same call.
    mutationFn: (input: { from: string; to: string; relation?: string; lag?: number }) =>
      api.post<Change>(`${base}/links`, {
        from_key: input.from,
        to_key: input.to,
        relation: input.relation ?? 'FS',
        lag_days: input.lag ?? 0,
      }),
    onSuccess: (result) => {
      toast(
        result.moved.length
          ? `Linked, and ${result.moved.map((m) => m.key).join(', ')} moved to suit.`
          : 'Linked.',
        'success',
      )
      refresh()
    },
    onError: fail,
  })

  const unlink = useMutation({
    mutationFn: (input: { from: string; to: string }) =>
      api.del(`${base}/links?from_key=${input.from}&to_key=${input.to}`),
    onSuccess: refresh,
    onError: fail,
  })

  return { move, amend, add, structure, reorder, remove, tick, link, unlink, tighten, refresh }
}

/**
 * What one edit did, in the form that puts it back.
 *
 * Undo used to know about drags only, so a date typed into the grid, a tick, a link set
 * from the predecessors cell or a row indented by mistake could not be undone — and a
 * button that works for some edits and silently not for others is a button people stop
 * trusting. Every edit now records what it changed, as the request that reverses it.
 * Deleting a task is the one thing that does not: the row is gone, and the tooltip on
 * the button says so.
 */
export type UndoAction =
  | { kind: 'moves'; moved: Moved[] }
  | { kind: 'patch'; key: string; body: Record<string, unknown> }
  | { kind: 'tick'; key: string; done: boolean }
  | { kind: 'link'; from: string; to: string; relation: string; lag: number }
  | { kind: 'unlink'; from: string; to: string }
  | { kind: 'remove'; key: string }
  | { kind: 'place'; key: string; after: string | null; parent: string | null }

/** One press of undo: everything a single edit did, put back in reverse order. */
export type UndoStep = UndoAction[]

/** The values a patch is about to overwrite, so the same patch can be sent back. */
export function previousOf(row: Row, body: Record<string, unknown>): Record<string, unknown> {
  const before: Record<string, unknown> = {}
  for (const field of Object.keys(body)) {
    switch (field) {
      case 'title': before.title = row.title; break
      case 'assignee_id': before.assignee_id = row.assignee_id; break
      case 'start_on': before.start_on = row.start ?? null; break
      case 'end_on': before.end_on = row.end ?? null; break
      case 'duration_days': before.duration_days = row.working_days ?? 1; break
      // A length typed in hours is undone by putting the days back. Same field, and the
      // days are what the row actually stored.
      case 'duration_hours': before.duration_days = row.working_days ?? 1; break
      case 'progress_pct': before.progress_pct = row.progress_pct === null ? null : Math.round(row.progress_pct ?? 0); break
      case 'is_milestone': before.is_milestone = row.is_milestone; break
      case 'priority': before.priority = row.priority; break
      case 'deadline_on': before.deadline_on = row.deadline_on; break
      case 'constraint_type': before.constraint_type = row.constraint_type; break
      case 'constraint_on': before.constraint_on = row.constraint_on; break
      case 'task_type': before.task_type = row.task_type; break
      case 'effort_driven': before.effort_driven = row.effort_driven; break
      case 'elapsed': before.elapsed = row.elapsed; break
      default: break
    }
  }
  return before
}

/** Where a row sits in the outline right now, as a reorder that would put it back. */
export function placeOf(row: Row, rows: Row[]): { after: string | null; parent: string | null } {
  const at = rows.findIndex((r) => r.key === row.key)
  const above = at > 0 ? rows[at - 1]! : null
  const parent = row.parent_id ? rows.find((r) => r.id === row.parent_id) : undefined
  return { after: above?.key ?? null, parent: parent?.key ?? null }
}

/**
 * The step that would put an undo back again, computed from the plan as it stands.
 *
 * Redo is undo's undo, and the cheapest correct way to get it is to record — at the
 * moment of undoing — what the undo is about to overwrite, exactly as an edit records
 * what it overwrites. A removed task is the one thing that cannot come back this way:
 * the row is gone, so its redo is nothing.
 */
export function redoStepFor(step: UndoStep, rows: Row[]): UndoStep {
  const byKey = new Map(rows.map((r) => [r.key, r]))
  const out: UndoStep = []
  for (const action of step) {
    switch (action.kind) {
      case 'moves':
        out.push({
          kind: 'moves',
          moved: action.moved.map((m) => ({
            ...m,
            // Swap the two ends: putting `was` back is undone by putting `start/end` back.
            start: m.was_start ?? m.start,
            end: m.was_end ?? m.end,
            was_start: m.start,
            was_end: m.end,
          })),
        })
        break
      case 'patch': {
        const row = byKey.get(action.key)
        if (row) out.push({ kind: 'patch', key: action.key, body: previousOf(row, action.body) })
        break
      }
      case 'tick':
        out.push({ kind: 'tick', key: action.key, done: !action.done })
        break
      case 'link': {
        const row = byKey.get(action.to)
        const had = row?.predecessors?.find((p) => p.key === action.from)
        out.push(
          had
            ? { kind: 'link', from: action.from, to: action.to, relation: had.relation, lag: had.lag_days }
            : { kind: 'unlink', from: action.from, to: action.to },
        )
        break
      }
      case 'unlink': {
        const row = byKey.get(action.to)
        const had = row?.predecessors?.find((p) => p.key === action.from)
        if (had) out.push({ kind: 'link', from: action.from, to: action.to, relation: had.relation, lag: had.lag_days })
        break
      }
      case 'place': {
        const row = byKey.get(action.key)
        if (row) {
          const now = placeOf(row, rows)
          out.push({ kind: 'place', key: action.key, after: now.after, parent: now.parent })
        }
        break
      }
      case 'remove':
        // Gone is gone.
        break
    }
  }
  // The undo runs its actions backwards; the redo has to run them forwards again, so
  // the recorded order is reversed to compensate for the reversal it will get.
  return out.reverse()
}

/** Put a whole change back — the edit and everything it pushed — in one act. */
export function useUndo(projectId: string, onDone: () => void) {
  const qc = useQueryClient()
  const toast = useToast()
  const base = `/projects/${projectId}/schedule`
  return useMutation({
    mutationFn: async (step: UndoStep) => {
      // Backwards, so a task lands before the thing that was pushed by it. Every date
      // is put back with cascade:false — the cascade is exactly what is being undone.
      for (const action of [...step].reverse()) {
        switch (action.kind) {
          case 'moves':
            for (const entry of [...action.moved].reverse()) {
              if (!entry.was_start || !entry.was_end) continue
              await api.post(`${base}/tasks/${entry.key}/move`, {
                start_on: entry.was_start,
                end_on: entry.was_end,
                cascade: false,
              })
            }
            break
          case 'patch':
            await api.patch(`${base}/tasks/${action.key}`, { ...action.body, cascade: false })
            break
          case 'tick':
            await api.post(`${base}/tasks/${action.key}/done`, { done: action.done })
            break
          case 'link':
            await api.post(`${base}/links`, {
              from_key: action.from,
              to_key: action.to,
              relation: action.relation,
              lag_days: action.lag,
            })
            break
          case 'unlink':
            await api.del(`${base}/links?from_key=${action.from}&to_key=${action.to}`)
            break
          case 'remove':
            await api.del(`${base}/tasks/${action.key}`)
            break
          case 'place':
            await api.post(`${base}/tasks/${action.key}/reorder`, {
              after_key: action.after,
              parent_key: action.parent,
            })
            break
        }
      }
    },
    onSuccess: () => {
      toast('Put back.', 'success')
      qc.invalidateQueries({ queryKey: ['plan', projectId] })
      qc.invalidateQueries({ queryKey: ['project-dashboard', projectId] })
      qc.invalidateQueries({ queryKey: ['work'] })
      onDone()
    },
    onError: (e: Error) => toast(`Could not put that back: ${e.message}`, 'danger'),
  })
}

/**
 * Focus an inline editor with its contents selected.
 *
 * Every grid in the world behaves this way — click a cell, type, and what you type
 * replaces what was there. Without it, typing appends to the old value and you end up
 * with "New taskPilot with two drivers", which is the sort of thing that makes a person
 * stop trusting a grid within about four seconds.
 */
export function selectOnFocus(el: HTMLInputElement | null) {
  if (!el) return
  el.focus()
  // select() is only meaningful — and only legal — on a text-ish input. Calling it on
  // a date field throws in Chrome, which would take the whole row down with it.
  if (el.type === 'text' || el.type === 'number' || el.type === '') el.select()
}
